import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface CrudHookOptions<T> {
  fetchFn: (projectId: string) => Promise<T[]>;
  createFn: (data: any) => Promise<T>;
  updateFn: (id: string, data: any) => Promise<T>;
  deleteFn: (id: string) => Promise<void>;
  projectId: string;
}

export function useCrud<T extends { _id: string }>({
  fetchFn,
  createFn,
  updateFn,
  deleteFn,
  projectId
}: CrudHookOptions<T>) {
  const { user } = useAuth();
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const canEdit = user?.role === 'superAdmin' || user?.role === 'admin' || user?.role === 'projectManager';
  const canDelete = user?.role === 'superAdmin' || user?.role === 'admin' || user?.role === 'projectManager';

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFn(projectId);
      setItems(data);
    } catch (err: any) {
      console.error('Failed to fetch items:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [fetchFn, projectId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (data: any) => {
    try {
      const newItem = await createFn({ ...data, project: projectId });
      setItems([...items, newItem]);
      setShowCreateModal(false);
      return { success: true, item: newItem };
    } catch (err: any) {
      console.error('Failed to create item:', err);
      return { success: false, error: err.message || 'Failed to create item' };
    }
  };

  const updateItem = async (id: string, data: any) => {
    try {
      const updatedItem = await updateFn(id, data);
      setItems(items.map(item => item._id === id ? updatedItem : item));
      setShowEditModal(false);
      setSelectedItem(null);
      return { success: true, item: updatedItem };
    } catch (err: any) {
      console.error('Failed to update item:', err);
      return { success: false, error: err.message || 'Failed to update item' };
    }
  };

  const deleteItem = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return { success: false };
    }

    try {
      await deleteFn(id);
      setItems(items.filter(item => item._id !== id));
      return { success: true };
    } catch (err: any) {
      console.error('Failed to delete item:', err);
      return { success: false, error: err.message || 'Failed to delete item' };
    }
  };

  const openEditModal = (item: T) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedItem(null);
  };

  return {
    items,
    loading,
    error,
    selectedItem,
    showCreateModal,
    showEditModal,
    canEdit,
    canDelete,
    setShowCreateModal,
    openEditModal,
    closeModals,
    createItem,
    updateItem,
    deleteItem,
    refreshItems: fetchItems
  };
}